import { classNames } from "primereact/utils";
import { Tooltip } from "react-tooltip";
import { Calendar } from "primereact/calendar";
import { createPortal } from "react-dom";
import styles from "./SlotsPanel.module.scss";
import { ITimeSlot } from "../models/employeeCalendarModels";
import { Icon, IconType } from "../../../../../icons/Icon";
import { RenderWhen, IfTrue } from "../../../../../infrastructure/components/RenderWhen";

interface ISlotsPanelProps {
  hasEmployee: boolean;
  hasDate: boolean;
  selectedDateText: string;
  groupedSlots: {
    morning: ITimeSlot[];
    afternoon: ITimeSlot[];
    evening: ITimeSlot[];
  };
  totalSlots: number;
  onSelectSlot: (slot: ITimeSlot) => void;

  // Custom-time inline section
  selectionMode: "slots" | "custom";
  customDateTime: Date | null;
  customConflictWarning: string;
  onOpenCustomMode: () => void;
  onCloseCustomMode: () => void;
  onCustomDateTimeChange: (value: Date | null) => void;
}

const TimeSlotButton = ({
  slot,
  index,
  onClick,
}: {
  slot: ITimeSlot;
  index: number;
  onClick: (slot: ITimeSlot) => void;
}) => {
  const tooltipId = `time-slot-tip-${index}-${slot.time.replace(":", "")}`;
  return (
    <button
      type="button"
      key={`${slot.time}-${index}`}
      data-tooltip-id={tooltipId}
      onClick={() => onClick(slot)}
      className={classNames(styles.slot, styles[`slot_${slot.status}`], {
        [styles.slot_selected]: slot.isSelected,
      })}
    >
      <span>{slot.time}</span>
      {createPortal(
        <Tooltip
          id={tooltipId}
          place="top"
          className="time-slot-tooltip"
          positionStrategy="fixed"
          style={{ zIndex: 2147483647 }}
        >
          {slot.tooltip}
        </Tooltip>,
        document.body
      )}
    </button>
  );
};

const SlotGroup = ({
  title,
  slots,
  onSelectSlot,
  indexOffset,
}: {
  title: string;
  slots: ITimeSlot[];
  onSelectSlot: (slot: ITimeSlot) => void;
  indexOffset: number;
}) => {
  if (slots.length === 0) return null;
  return (
    <div className={styles.group}>
      <div className={styles.group_header}>{title}</div>
      <div className={styles.group_grid}>
        {slots.map((slot, i) => (
          <TimeSlotButton
            key={`${title}-${slot.time}`}
            slot={slot}
            index={indexOffset + i}
            onClick={onSelectSlot}
          />
        ))}
      </div>
    </div>
  );
};

export const SlotsPanel = ({
  hasEmployee,
  hasDate,
  selectedDateText,
  groupedSlots,
  totalSlots,
  onSelectSlot,
  selectionMode,
  customDateTime,
  customConflictWarning,
  onOpenCustomMode,
  onCloseCustomMode,
  onCustomDateTimeChange,
}: ISlotsPanelProps) => {
  const showEmptyNoEmployee = !hasEmployee;
  const showEmptyNoDate = hasEmployee && !hasDate;
  const showEmptyClosed = hasEmployee && hasDate && totalSlots === 0;
  const showSlots = hasEmployee && hasDate && totalSlots > 0;

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <Icon type={IconType.ClockIcon} color="rgba(255,255,255,0.75)" />
        <div>
          <div className={styles.header_title}>Свободни часове</div>
          <div className={styles.header_subtitle}>
            {hasDate ? selectedDateText : "Изберете дата от календара"}
          </div>
        </div>
      </div>

      <div className={styles.body}>
        <RenderWhen condition={showEmptyNoEmployee}>
          <IfTrue>
            <div className={styles.empty_state}>
              <Icon type={IconType.Person} color="rgba(255,255,255,0.4)" height="28px" width="28px" />
              <div>Изберете служител, за да видите наличните часове.</div>
            </div>
          </IfTrue>
        </RenderWhen>

        <RenderWhen condition={showEmptyNoDate}>
          <IfTrue>
            <div className={styles.empty_state}>
              <Icon type={IconType.Calendar} color="rgba(255,255,255,0.4)" height="28px" width="28px" />
              <div>Изберете дата, за да видите наличните часове.</div>
            </div>
          </IfTrue>
        </RenderWhen>

        <RenderWhen condition={showEmptyClosed}>
          <IfTrue>
            <div className={styles.empty_state}>
              <Icon type={IconType.Calendar} color="rgba(255,255,255,0.4)" height="28px" width="28px" />
              <div>
                На избраната дата служителят почива.
                <br />
                Използвайте „Точен час“ по-долу, за да добавите резервация.
              </div>
            </div>
          </IfTrue>
        </RenderWhen>

        <RenderWhen condition={showSlots}>
          <IfTrue>
            <div className={styles.groups}>
              <SlotGroup
                title="Сутрин"
                slots={groupedSlots.morning}
                onSelectSlot={onSelectSlot}
                indexOffset={0}
              />
              <SlotGroup
                title="Следобед"
                slots={groupedSlots.afternoon}
                onSelectSlot={onSelectSlot}
                indexOffset={groupedSlots.morning.length}
              />
              <SlotGroup
                title="Вечер"
                slots={groupedSlots.evening}
                onSelectSlot={onSelectSlot}
                indexOffset={
                  groupedSlots.morning.length + groupedSlots.afternoon.length
                }
              />
            </div>
          </IfTrue>
        </RenderWhen>
      </div>

      <div
        className={classNames(styles.custom_section, {
          [styles.custom_section_open]: selectionMode === "custom",
        })}
      >
        <RenderWhen condition={selectionMode !== "custom"}>
          <IfTrue>
            <button
              type="button"
              className={styles.custom_toggle}
              onClick={onOpenCustomMode}
              disabled={!hasEmployee}
            >
              <Icon type={IconType.ClockIcon} color="currentColor" />
              <span>Точен час (извън списъка)</span>
            </button>
          </IfTrue>
        </RenderWhen>

        <RenderWhen condition={selectionMode === "custom"}>
          <IfTrue>
            <div className={styles.custom_card}>
              <div className={styles.custom_card_header}>
                <div className={styles.custom_card_title}>Точен час</div>
                <button
                  type="button"
                  className={styles.custom_card_close}
                  onClick={onCloseCustomMode}
                  aria-label="Затвори секцията"
                >
                  ×
                </button>
              </div>

              <div className={styles.custom_card_description}>
                Изберете точна дата и час, които не са в списъка със свободни часове.
              </div>

              <Calendar
                value={customDateTime}
                onChange={(e) => onCustomDateTimeChange(e.value ?? null)}
                showTime
                hourFormat="24"
                stepMinute={15}
                dateFormat="dd/mm/yy"
                placeholder="Изберете дата и час"
                className={styles.custom_picker}
                showIcon
                touchUI
                icon={<Icon type={IconType.ClockIcon} color="currentColor" />}
              />

              {customConflictWarning && (
                <div className={styles.custom_warning}>
                  <div className={styles.custom_warning_title}>
                    ⚠️ Открит е конфликт
                  </div>
                  {customConflictWarning.split("\n").map((line, i) => (
                    <div key={i} className={styles.custom_warning_line}>
                      {line}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </IfTrue>
        </RenderWhen>
      </div>
    </div>
  );
};
